<script lang="ts" setup>
import {
  tpgIconDelete,
  tpgIconEdit,
  tpgIconMarkChatRead,
  tpgIconMarkChatUnread,
  tpgIconMoreHoriz,
  tpgIconNotificationsActive,
  tpgIconNotificationsOff,
  tpgIconOpenInBrowser,
  tpgIconPushPin,
  tpgIconUnpinCustom,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarMenuItemProps,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarMenuItem,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import type { ConversationListResultDTO } from '@nora-monorepo/nora-api-client';
import {
  useConfirmModalStore,
  useDeviceStore,
} from '@nora-monorepo/ui-shared';

import { useConversationDetailsModal } from '#nora-chat-ui/components/modals/new-conversation/conversation-details-modal-context';
import type { ConversationState } from '#nora-chat-ui/states';
import { useConversationStore } from '#nora-chat-ui/stores/conversation/conversation-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { chatCan } from '#nora-chat-ui/utils/permission';

interface Props {
  conversationState: ConversationState;
}

const props = defineProps<Props>();

const { conversationState } = toRefs(props);

const { t } = useI18n();
const userStore = useChatUserStore();
const conversationStore = useConversationStore();
const deviceStore = useDeviceStore();
const confirmModalStore = useConfirmModalStore();
const conversationDetailsModal = useConversationDetailsModal();

const { isMobile } = storeToRefs(deviceStore);
const { showConfirmModal } = confirmModalStore;

const conversation = computed<ConversationListResultDTO>(
  () => props.conversationState.conversationListResult,
);

function deleteConversation() {
  conversationStore.deleteConversation(conversation.value.id);
}

function showConfirmDeleteModal() {
  showConfirmModal({
    headerText: t(`conversation.deleteConversationTitle`),
    bodyText: conversation.value?.title
      ? t(`conversation.deleteConversationMessageWithTitle`, [
          conversation.value.title,
        ])
      : t(`conversation.deleteConversationMessage`),
    onConfirm: deleteConversation,
    defaultButtonFocus: 'confirm',
    isDelete: true,
  });
}

const menuItems = computed((): GrammarMenuItemProps[] => {
  const items: GrammarMenuItemProps[] = [];
  items.push(
    {
      label: t('conversation.details'),
      leadingIconTemplate: tpgIconEdit,
      onSelect: () => {
        return conversationDetailsModal.openDetails(conversation.value.id);
      },
    },
    {
      label: userStore.isPinned(
        conversation.value.id,
      )
        ? 'Unpin'
        : 'Pin',
      leadingIconTemplate: userStore.isPinned(
        conversation.value.id,
      )
        ? tpgIconUnpinCustom
        : tpgIconPushPin,
      onSelect: () => {
        conversationStore.toggleConversationPin(
          conversation.value.id,
        );
      },
    },
  );

  if (!isMobile.value) {
    items.push({
      label: t('conversation.openInTab'),
      leadingIconTemplate: tpgIconOpenInBrowser,
      disabled: conversationState.value.openIndex !== null,
      onSelect: () => {
        conversationStore.openConversationInNewTab(
          conversation.value.id,
        );
      },
    });
  }

  items.push({
    label: !conversation.value.isMuted
      ? t('conversation.configureNotifications')
      : t('conversation.unMute'),
    leadingIconTemplate: !conversation.value.isMuted
      ? tpgIconNotificationsOff
      : tpgIconNotificationsActive,
    onSelect: () => {
      if (conversation.value.isMuted) {
        conversationStore.unMuteConversation(conversation.value.id);
      }
      else {
        conversationStore.muteConversation(conversation.value.id);
      }
    },
  });
  items.push({
    label: conversation.value.isRead
      ? t('conversation.markAsUnread')
      : t('conversation.markAsRead'),
    leadingIconTemplate: conversation.value.isRead
      ? tpgIconMarkChatUnread
      : tpgIconMarkChatRead,
    onSelect: () => {
      if (conversation.value.isRead) {
        conversationStore.markConversationAsUnread(conversation.value.id);
      }
      else {
        conversationStore.markConversationAsRead(conversation.value.id);
      }
    },
  });

  if (
    chatCan(
      'delete:chat.conversation',
      conversation.value.id,
    )
  ) {
    items.push({
      label: t('common.delete'),
      leadingIconTemplate: tpgIconDelete,
      // class: `${textRed} hover:text-red-light dark:hover:text-red-dark`, // Just keeping this so we remember it should be red when grammar menu items can be red
      onSelect: () => showConfirmDeleteModal(),
    });
  }

  return items;
});
</script>

<template>
  <GrammarQuaternaryMenuButton
    :icon-template="tpgIconMoreHoriz"
    size="small"
    @click="conversationStore.setOpenConversationMenu(conversation.id)"
    @click.stop
  >
    <GrammarMenuItem
      v-for="(item, key) in menuItems"
      :key="key"
      v-bind="item"
    />
  </GrammarQuaternaryMenuButton>
</template>
